import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Drawer,
  Tabs,
  Button,
  Table,
  Tag,
  Space,
  Modal,
  Form,
  Input,
  Select,
  Switch,
  InputNumber,
  Tooltip,
  Typography,
  Alert,
  message,
  Popconfirm,
  Badge
} from "antd";
import {
  CameraOutlined,
  DeleteOutlined,
  PlusOutlined,
  ReloadOutlined,
  ClockCircleOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  SyncOutlined,
  ScheduleOutlined,
  RollbackOutlined
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import {
  fetchSnapshots,
  takeSnapshot,
  removeSnapshot,
  fetchPolicies,
  addPolicy,
  updatePolicy,
  removePolicy
} from "./store/ebsSnapshotSlice";
import { launchLinuxDesktop } from "../LinuxDesktops/store/linuxSlice";
import { launchWindowsDesktop } from "../WindowsDesktops/store/windowsSlice";

const { Text } = Typography;
const { Option } = Select;

const SNAPSHOT_STATE_CONFIG = {
  Pending:     { color: "processing", icon: <SyncOutlined spin /> },
  Stopping:    { color: "warning",    icon: <PauseCircleOutlined /> },
  Creating:    { color: "processing", icon: <SyncOutlined spin /> },
  Restarting:  { color: "warning",    icon: <PlayCircleOutlined /> },
  Registering: { color: "processing", icon: <SyncOutlined spin /> },
  Ready:       { color: "success",    icon: <CheckCircleOutlined /> },
  Failed:      { color: "error",      icon: <ExclamationCircleOutlined /> }
};

function stateTag(state) {
  const cfg = SNAPSHOT_STATE_CONFIG[state] || { color: "default", icon: null };
  return <Tag color={cfg.color} icon={cfg.icon}>{state || "Unknown"}</Tag>;
}

function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(undefined, {
    month: "short", day: "numeric",
    hour: "2-digit", minute: "2-digit"
  });
}

// ─── Take Snapshot Modal ──────────────────────────────────────────────────────

function TakeSnapshotModal({ desktopRef, open, onClose, onDone }) {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    const values = await form.validateFields();
    setLoading(true);
    try {
      await dispatch(takeSnapshot({
        desktopRef,
        displayName: values.displayName?.trim(),
        visibility:  values.visibility || "private",
        volumeType:  values.volumeType,
        consistency: values.consistency,
        description: values.description
      })).unwrap();
      message.success(
        `Snapshot "${values.displayName?.trim()}" started — once it's ready it will appear on your home page as a new desktop.`
      );
      form.resetFields();
      onDone();
    } catch (err) {
      message.error(err?.message || String(err) || "Failed to create snapshot");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={<><CameraOutlined /> Take Snapshot</>}
      open={open}
      onCancel={onClose}
      onOk={handleSubmit}
      okText="Take Snapshot"
      confirmLoading={loading}
      destroyOnClose
      centered
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{ volumeType: "both", consistency: "crash-consistent", visibility: "private" }}
      >
        <Form.Item label="Desktop" style={{ marginBottom: 8 }}>
          <Text strong>{desktopRef}</Text>
        </Form.Item>

        <Form.Item
          name="displayName"
          label="Name"
          tooltip="This name is shown on your home page as the new desktop entry created from this snapshot."
          rules={[
            { required: true, whitespace: true, message: "Please name this snapshot" },
            { max: 128, message: "Max 128 characters" }
          ]}
        >
          <Input placeholder="e.g. my-configured-workstation" autoFocus maxLength={128} />
        </Form.Item>

        <Form.Item
          name="visibility"
          label="Visibility"
          tooltip="Private: only you can see and use this snapshot. Public: everyone in your organization can see it and launch desktops from it."
          rules={[{ required: true }]}
        >
          <Select>
            <Option value="private">Private (only me)</Option>
            <Option value="public">Public (whole organization)</Option>
          </Select>
        </Form.Item>

        <Form.Item name="volumeType" label="Volume scope" rules={[{ required: true }]}>
          <Select>
            <Option value="both">Both (root + data)</Option>
            <Option value="root">Root disk only</Option>
            <Option value="data">Data disk only</Option>
          </Select>
        </Form.Item>

        <Form.Item name="consistency" label="Consistency" rules={[{ required: true }]}>
          <Select>
            <Option value="crash-consistent">Crash-consistent (no downtime)</Option>
            <Option value="stopped">Application-consistent (stops instance)</Option>
          </Select>
        </Form.Item>

        <Form.Item name="description" label="Description (optional)">
          <Input placeholder="e.g. Before major update" />
        </Form.Item>
      </Form>
    </Modal>
  );
}

// ─── Create Policy Modal ──────────────────────────────────────────────────────

function CreatePolicyModal({ desktopRef, open, onClose, onDone }) {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    const values = await form.validateFields();
    setLoading(true);
    try {
      await dispatch(addPolicy({
        desktopRef,
        schedule:    values.schedule,
        volumeType:  values.volumeType,
        consistency: values.consistency,
        retention: {
          count:  values.retentionCount || 0,
          maxAge: values.maxAge || ""
        }
      })).unwrap();
      message.success("Scheduled policy created.");
      form.resetFields();
      onDone();
    } catch (err) {
      message.error(err?.message || String(err) || "Failed to create policy");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={<><ScheduleOutlined /> Create Snapshot Policy</>}
      open={open}
      onCancel={onClose}
      onOk={handleSubmit}
      okText="Create Policy"
      confirmLoading={loading}
      destroyOnClose
      centered
      width={480}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{ volumeType: "both", consistency: "crash-consistent", retentionCount: 7 }}
      >
        <Form.Item label="Desktop" style={{ marginBottom: 8 }}>
          <Text strong>{desktopRef}</Text>
        </Form.Item>

        <Form.Item
          name="schedule"
          label="Cron schedule (UTC)"
          rules={[{ required: true, message: "Enter a 5-field cron expression" }]}
          tooltip="5-field UTC cron: minute hour dom month dow. E.g. '0 2 * * *' = daily at 02:00"
        >
          <Input placeholder="0 2 * * *" />
        </Form.Item>

        <Form.Item name="volumeType" label="Volume scope" rules={[{ required: true }]}>
          <Select>
            <Option value="both">Both (root + data)</Option>
            <Option value="root">Root disk only</Option>
            <Option value="data">Data disk only</Option>
          </Select>
        </Form.Item>

        <Form.Item name="consistency" label="Consistency" rules={[{ required: true }]}>
          <Select>
            <Option value="crash-consistent">Crash-consistent</Option>
            <Option value="stopped">Application-consistent (stops instance)</Option>
          </Select>
        </Form.Item>

        <Form.Item name="retentionCount" label="Keep last N snapshots (0 = unlimited)">
          <InputNumber min={0} max={999} style={{ width: "100%" }} />
        </Form.Item>

        <Form.Item name="maxAge" label="Delete snapshots older than (e.g. 720h, 30d)">
          <Input placeholder="720h" />
        </Form.Item>
      </Form>
    </Modal>
  );
}

// ─── Restore Desktop Modal ────────────────────────────────────────────────────

function RestoreDesktopModal({ snapshot, open, onClose, onDone }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  // Route the restore to the desktop type the snapshot was taken from —
  // a Windows snapshot must boot a Windows desktop and vice versa.
  const isWindows = snapshot?.osType === "windows";

  const handleSubmit = async () => {
    const values = await form.validateFields();
    setLoading(true);
    try {
      const params = {
        template: isWindows ? "windows" : "ubuntu",
        instanceType: values.instanceType || undefined,
        restore: { fromSnapshot: snapshot.name }
      };
      const desk = await dispatch(
        isWindows ? launchWindowsDesktop(params) : launchLinuxDesktop(params)
      ).unwrap();
      message.success("New desktop is provisioning from the snapshot.");
      form.resetFields();
      onDone();
      if (desk?.id) navigate(`/control/${isWindows ? "windows" : "linux"}/${desk.id}`);
    } catch (err) {
      message.error(err?.message || String(err) || "Failed to create desktop");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={<><RollbackOutlined /> Restore to New Desktop</>}
      open={open}
      onCancel={onClose}
      onOk={handleSubmit}
      okText="Create Desktop"
      confirmLoading={loading}
      destroyOnClose
      centered
    >
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 16 }}
        message="A new desktop will be created with volumes restored from this snapshot. Your original desktop is not affected."
      />
      <Form form={form} layout="vertical">
        <Form.Item label="Snapshot">
          <Text strong>{snapshot?.displayName || snapshot?.name}</Text>
        </Form.Item>
        <Form.Item label="Desktop type">
          <Tag color={isWindows ? "blue" : "orange"}>{isWindows ? "Windows" : "Linux"}</Tag>
        </Form.Item>
        <Form.Item
          name="instanceType"
          label="Instance type (optional)"
          tooltip="Leave blank to use the org default (g4dn.xlarge)"
        >
          <Input placeholder="e.g. g4dn.xlarge" />
        </Form.Item>
      </Form>
    </Modal>
  );
}

// ─── Snapshots Tab ────────────────────────────────────────────────────────────

function SnapshotsTab({ desktopRef }) {
  const dispatch = useDispatch();
  const snapshotState = useSelector((s) => s.ebsSnapshots);
  const key = desktopRef || "all";
  const snapshots = useMemo(() => snapshotState.snapshots[key] || [], [snapshotState.snapshots, key]);
  const loading = snapshotState.snapshotLoading;
  const deletingSnapshots = snapshotState.deletingSnapshots;

  const [takeOpen, setTakeOpen] = useState(false);
  const [restoreSnapshot, setRestoreSnapshot] = useState(null);

  const load = useCallback(() => {
    dispatch(fetchSnapshots(desktopRef));
  }, [dispatch, desktopRef]);

  useEffect(() => {
    load();
  }, [load]);

  // Poll while any snapshot is still in flight so users see it reach Ready
  // (and become restorable) without having to click Refresh.
  useEffect(() => {
    const anyInFlight = snapshots.some(
      (s) => s.state && s.state !== "Ready" && s.state !== "Failed"
    );
    if (!anyInFlight) return undefined;
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [snapshots, load]);

  const handleDelete = async (name) => {
    try {
      await dispatch(removeSnapshot(name)).unwrap();
      message.success(`Snapshot ${name} deleted.`);
    } catch (err) {
      message.error(err?.message || String(err) || "Failed to delete snapshot");
    }
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "displayName",
      key: "displayName",
      render: (displayName, record) => (
        <Tooltip title={record.name}>
          <Text strong>{displayName || record.name}</Text>
        </Tooltip>
      )
    },
    {
      title: "State",
      dataIndex: "state",
      key: "state",
      width: 140,
      render: (state) => stateTag(state)
    },
    {
      title: "Visibility",
      dataIndex: "visibility",
      key: "visibility",
      width: 100,
      render: (visibility) => (
        <Tooltip
          title={
            visibility === "public"
              ? "Visible to your whole organization"
              : "Visible only to you"
          }
        >
          <Tag color={visibility === "public" ? "geekblue" : "default"}>
            {visibility === "public" ? "Public" : "Private"}
          </Tag>
        </Tooltip>
      )
    },
    {
      title: "Scope",
      dataIndex: "volumeType",
      key: "volumeType",
      width: 100,
      render: (v) => <Tag>{v}</Tag>
    },
    {
      title: "Volumes",
      dataIndex: "volumes",
      key: "volumes",
      render: (vols) => {
        if (!vols?.length) return <Text type="secondary">—</Text>;
        return (
          <Space direction="vertical" size={2}>
            {vols.map((v) => (
              <Space key={v.device} size={4}>
                <Text style={{ fontSize: 12 }}>{v.device}</Text>
                {stateTag(v.state)}
                {v.progress && <Text type="secondary" style={{ fontSize: 11 }}>{v.progress}</Text>}
              </Space>
            ))}
          </Space>
        );
      }
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 140,
      render: formatDate
    },
    {
      title: "",
      key: "actions",
      width: 90,
      render: (_, record) => (
        <Space size={4}>
          <Tooltip
            title={
              record.restorable
                ? "Restore to new desktop"
                : record.notRestorableReason || "Not ready to restore yet"
            }
          >
            <Button
              type="text"
              icon={<RollbackOutlined />}
              disabled={!record.restorable}
              onClick={() => setRestoreSnapshot(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Delete this snapshot?"
            description="The EBS snapshot will be permanently removed."
            okText="Delete"
            okType="danger"
            onConfirm={() => handleDelete(record.name)}
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              loading={!!deletingSnapshots[record.name]}
              disabled={record.state === "Creating" || record.state === "Stopping"}
            />
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginBottom: 12 }}>
        <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Refresh</Button>
        <Button type="primary" icon={<CameraOutlined />} onClick={() => setTakeOpen(true)}>
          Take Snapshot
        </Button>
      </div>

      <Table
        dataSource={snapshots}
        columns={columns}
        rowKey="name"
        loading={loading}
        size="small"
        pagination={{ pageSize: 10, hideOnSinglePage: true }}
        locale={{ emptyText: "No snapshots yet. Click 'Take Snapshot' to create one." }}
      />

      <TakeSnapshotModal
        desktopRef={desktopRef}
        open={takeOpen}
        onClose={() => setTakeOpen(false)}
        onDone={() => { setTakeOpen(false); load(); }}
      />

      {restoreSnapshot && (
        <RestoreDesktopModal
          snapshot={restoreSnapshot}
          open={!!restoreSnapshot}
          onClose={() => setRestoreSnapshot(null)}
          onDone={() => setRestoreSnapshot(null)}
        />
      )}
    </>
  );
}

// ─── Policies Tab ─────────────────────────────────────────────────────────────

function PoliciesTab({ desktopRef }) {
  const dispatch = useDispatch();
  const policyState = useSelector((s) => s.ebsSnapshots);
  const key = desktopRef || "all";
  const policies = policyState.policies[key] || [];
  const loading = policyState.policyLoading;
  const deletingPolicies = policyState.deletingPolicies;

  const [createOpen, setCreateOpen] = useState(false);

  const load = useCallback(() => {
    dispatch(fetchPolicies(desktopRef));
  }, [dispatch, desktopRef]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSuspend = async (policy, suspend) => {
    try {
      await dispatch(updatePolicy({ name: policy.name, patch: { suspend } })).unwrap();
      message.success(suspend ? "Policy suspended." : "Policy resumed.");
    } catch (err) {
      message.error(err?.message || String(err) || "Failed to update policy");
    }
  };

  const handleDelete = async (name) => {
    try {
      await dispatch(removePolicy(name)).unwrap();
      message.success(`Policy ${name} deleted.`);
    } catch (err) {
      message.error(err?.message || String(err) || "Failed to delete policy");
    }
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (name) => <Text style={{ fontFamily: "monospace", fontSize: 12 }}>{name}</Text>
    },
    {
      title: "Schedule",
      dataIndex: "schedule",
      key: "schedule",
      width: 130,
      render: (s) => <Tag icon={<ClockCircleOutlined />}>{s}</Tag>
    },
    {
      title: "Status",
      dataIndex: "suspend",
      key: "suspend",
      width: 100,
      render: (suspend) => suspend
        ? <Badge status="warning" text="Suspended" />
        : <Badge status="processing" text="Active" />
    },
    {
      title: "Retention",
      key: "retention",
      width: 130,
      render: (_, p) => {
        if (!p.retention) return <Text type="secondary">—</Text>;
        const parts = [];
        if (p.retention.count > 0) parts.push(`${p.retention.count} snapshots`);
        if (p.retention.maxAge) parts.push(`${p.retention.maxAge}`);
        return <Text style={{ fontSize: 12 }}>{parts.join(", ") || "—"}</Text>;
      }
    },
    {
      title: "Last run",
      dataIndex: "lastScheduleTime",
      key: "lastScheduleTime",
      width: 140,
      render: formatDate
    },
    {
      title: "Next run",
      dataIndex: "nextScheduleTime",
      key: "nextScheduleTime",
      width: 140,
      render: (t, p) => p.suspend ? <Text type="secondary">—</Text> : formatDate(t)
    },
    {
      title: "Fired",
      dataIndex: "snapshotsCreated",
      key: "snapshotsCreated",
      width: 60,
      render: (n) => <Text type="secondary">{n}</Text>
    },
    {
      title: "",
      key: "actions",
      width: 100,
      render: (_, p) => (
        <Space>
          <Tooltip title={p.suspend ? "Resume" : "Suspend"}>
            <Switch
              size="small"
              checked={!p.suspend}
              onChange={(checked) => handleSuspend(p, !checked)}
            />
          </Tooltip>
          <Popconfirm
            title="Delete this policy?"
            description="Existing snapshots are preserved."
            okText="Delete"
            okType="danger"
            onConfirm={() => handleDelete(p.name)}
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              loading={!!deletingPolicies[p.name]}
            />
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginBottom: 12 }}>
        <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Refresh</Button>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
          New Policy
        </Button>
      </div>

      <Table
        dataSource={policies}
        columns={columns}
        rowKey="name"
        loading={loading}
        size="small"
        pagination={{ pageSize: 10, hideOnSinglePage: true }}
        locale={{ emptyText: "No scheduled policies. Click 'New Policy' to set one up." }}
      />

      <CreatePolicyModal
        desktopRef={desktopRef}
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onDone={() => { setCreateOpen(false); load(); }}
      />
    </>
  );
}

// ─── Main Drawer ──────────────────────────────────────────────────────────────

function SnapshotDrawer({ desktopRef, open, onClose }) {
  const snapshotCount = useSelector((s) => {
    const key = desktopRef || "all";
    return (s.ebsSnapshots.snapshots[key] || []).length;
  });

  const tabItems = [
    {
      key: "snapshots",
      label: (
        <span>
          <CameraOutlined /> Snapshots
          {snapshotCount > 0 && (
            <Tag color="blue" style={{ marginLeft: 6, fontSize: 11 }}>{snapshotCount}</Tag>
          )}
        </span>
      ),
      children: <SnapshotsTab desktopRef={desktopRef} />
    },
    {
      key: "policies",
      label: <span><ScheduleOutlined /> Scheduled Policies</span>,
      children: <PoliciesTab desktopRef={desktopRef} />
    }
  ];

  return (
    <Drawer
      title={
        <Space>
          <CameraOutlined />
          <span>EBS Snapshots</span>
          {desktopRef && <Tag style={{ marginLeft: 4 }}>{desktopRef}</Tag>}
        </Space>
      }
      open={open}
      onClose={onClose}
      width={820}
      destroyOnClose
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="EBS snapshots are stored in AWS and billed by AWS. Snapshots survive desktop deletion."
      />
      <Tabs defaultActiveKey="snapshots" items={tabItems} />
    </Drawer>
  );
}

export default SnapshotDrawer;
